import { useStyles, Dialog, pxToRem, Grid } from '@archipro-design/aria';

import * as S from './CompanyImages.style';
import type { CompanyImage } from '@modules/professional/type/company-images';
import ImageSlide from './ImageSlide';
import { CloseLine } from '@archipro-design/icons';
import { useEffect, useRef, useState } from 'react';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

const SLIDE_HEIGHT = 840;
const SLIDE_WIDTH_LANDSCAPE = 1240;

interface PhotoViewerModalProps {
    professional: {
        id: number;
        title: string;
        companyLogoSrc: string;
        logoBackgroundColor: string;
        blockSummary: string;
    };
    stack: Array<CompanyImage>;
    imageId: number;
    open: boolean;
    toggle: () => void;
}

interface SlideProps {
    key: string;
    id: number;
    content: JSX.Element;
}

const PhotoViewerModal = ({
    stack,
    open,
    toggle,
    imageId,
}: PhotoViewerModalProps) => {
    const divRefs = useRef(new Map<number, HTMLDivElement>());
    const [scrolled, setScrolled] = useState(false);
    const { css } = useStyles({ scrolled });

    const items: SlideProps[] = stack.flatMap((slide, idx) => {
        const displayWidth = slide.isPortrait
            ? slide.widthAfterCrop
            : SLIDE_WIDTH_LANDSCAPE;

        return {
            key: `${idx}-ImageSlide`,
            id: slide.ID,
            content: (
                <div
                    className={css(S.StackSlideContainer)}
                    ref={(ref) => ref && divRefs.current.set(slide.ID, ref)}
                >
                    <BoostResolutionOverlay
                        libraryImageId={slide.ID}
                        currentImageUrl={slide.src}
                        displayWidth={displayWidth}
                        displayHeight={SLIDE_HEIGHT}
                        wasUpscaled={!!slide.wasUpscaled}
                        className="mx-auto w-fit"
                    >
                        <ImageSlide slide={slide} />
                    </BoostResolutionOverlay>
                </div>
            ),
        };
    });

    useEffect(() => {
        if (!open) return;

        setTimeout(() => {
            divRefs.current.get(imageId)?.scrollIntoView();
            setScrolled(true);
        }, 200);
    }, [imageId, divRefs, open]);

    return (
        <Dialog
            open={open}
            onCancel={() => toggle()}
            className={css(S.PhotoViewerDialog)}
            headerAction={{
                icon: (
                    <CloseLine
                        className={css({
                            width: `${pxToRem(40)} !important`,
                            height: `${pxToRem(40)} !important`,
                        })}
                    />
                ),
                title: 'Close',
                onClick: () => toggle(),
            }}
            content={{
                content: (
                    <Grid columns={1} className={css(S.StackContainer)}>
                        {items.map((s) => s.content)}
                    </Grid>
                ),
            }}
            variables={{
                rootWidth: pxToRem(1920),
                rootMaxHeight: '100vh',
                overflow: true,
            }}
        />
    );
};

export default PhotoViewerModal;
